import type { NarrationService } from '../../sdk/narration';
import type {
  TeachingContext,
  TeachingMomentController,
  TeachingMomentDefinition,
  TeachingMomentOutcome,
  TeachingProvider,
} from '../../sdk/teaching';
import type { PlayerId, TeachingContentId } from '../../shared/types/ids';
import type { MissionRuntimeObserver, TeachingDelivery, TeachingTrigger } from './mission-events';
import { createTimerGroup } from './round-timers';
import type { Scheduler } from './scheduler';

/**
 * Platform-owned teaching lifecycle (module spec §14).
 *
 * The game supplies the script; the platform decides *whether it runs in full*. Two caps, and
 * the second is the interesting one:
 *
 *   - one full teaching moment per player per mission. Additional misses receive shorter hints
 *     rather than repeated full interruptions (MVP notes §11).
 *   - the scripted round-9 moment and the adaptive one are the same call. So if the adaptive one
 *     already fired, round 9 degrades to a hint by construction rather than by the caller
 *     remembering to check (D-034 PS-5). Nothing that calls this can accidentally hand a child a
 *     second full interruption.
 *
 * A moment that cannot be fetched is reported, not thrown: a missing script is a content gap, and
 * stopping a five-year-old's mission over one is a worse outcome than carrying on without it.
 */

export interface TeachingRunResult {
  delivery: TeachingDelivery;
  outcome: TeachingMomentOutcome;
  /**
   * The moment that actually ran, or nothing when none could be fetched.
   *
   * The runtime needs it to ask the authored `confirmationContentId` immediately afterwards —
   * the whole point of a teaching moment is that the child gets to answer something they can now
   * do, rather than being handed the question they just missed again (MVP notes §11, R1-2).
   */
  definition?: TeachingMomentDefinition;
}

export interface TeachingRunOptions {
  /** Defaults to `LADDER`; the proactive, content-declared moment passes `SCRIPTED`. */
  trigger?: TeachingTrigger;
  /**
   * Called — and awaited — the instant the one-per-mission cap is spent, before a word of the
   * moment is delivered (R1-3).
   *
   * The runtime uses it to write the round's snapshot. The ordering is the whole point: the cap
   * is spent in memory here, and a tablet closed during the twelve seconds that follow must not
   * restore a state in which the moment never happened. Only a `FULL` delivery spends anything,
   * so only a `FULL` delivery calls this.
   */
  onCapSpent?: () => Promise<void>;
}

export interface MissionTeachingController extends TeachingMomentController {
  /** The runtime's richer entry point: it needs to know *how much* teaching happened. */
  deliver(
    teachingContentId: TeachingContentId,
    context: TeachingContext,
    options?: TeachingRunOptions,
  ): Promise<TeachingRunResult>;
  /** Players whose one full moment has been spent — persisted for an interrupted session. */
  consumedPlayerIds(): PlayerId[];
  restore(playerIds: readonly PlayerId[]): void;
  /**
   * Suspends the moment's own duration cap (D-036(9)).
   *
   * The cap used to run on the raw scheduler while the round timers were suspendable, so a
   * teaching moment paused for a knock at the door carried on expiring behind the overlay.
   */
  pause(): void;
  resume(): void;
  /**
   * Ends whatever is running, now.
   *
   * "End session safely" is only reachable from the pause overlay (D-041(1)), and a paused
   * moment's own timer is suspended — so without this the sitting could not be closed until the
   * grown-up pressed Resume, which is the opposite of what they asked for (R1-5).
   */
  cancel(): void;
}

export interface MissionTeachingControllerOptions {
  provider: TeachingProvider;
  narration: NarrationService;
  scheduler: Scheduler;
  onEvent?: MissionRuntimeObserver;
}

export function createMissionTeachingController(
  options: MissionTeachingControllerOptions,
): MissionTeachingController {
  const consumed = new Set<PlayerId>();
  // Its own group rather than the runtime's round timers: the runtime suspends the round ladder
  // *around* a teaching moment, and a moment waiting on the group it just paused never ends.
  const timers = createTimerGroup(options.scheduler);

  let paused = false;
  /** Wall-clock spent behind the pause overlay, which is not teaching time. */
  let pausedTotalMs = 0;
  let pausedSinceMs: number | null = null;
  /** Resolvers waiting for the mission to come back, so narration cannot run behind a pause. */
  let resumeWaiters: (() => void)[] = [];
  /** Set while a moment is running; called to end it early (R1-5). */
  let cancelCurrent: (() => void) | null = null;
  /**
   * Set while a narration line is in the air; stops that utterance and hands the line back to the
   * loop so it can be said again (R1-4).
   */
  let interruptLine: (() => void) | null = null;
  /**
   * One token per delivery, created before that delivery's first await (R3-1).
   *
   * A single shared `cancelled` flag could not survive the delivery's own first await. The cap
   * checkpoint is written *before* the moment starts, so "End session safely" can land in a window
   * where there is no line, no timer and no gate to cancel — and a flag the delivery reset once it
   * got going erased it. A token exists from the first instant the delivery can be interrupted,
   * is never un-cancelled, and every gate the delivery opens afterwards checks it, so a cancel can
   * neither be forgotten nor overtaken by a gate created after it.
   */
  const liveTokens = new Set<{ cancelled: boolean }>();

  const wait = (token: { cancelled: boolean }, durationMs: number): Promise<void> => {
    // A gate opened after the cancel is already open.
    if (token.cancelled) return Promise.resolve();
    return new Promise((resolve) => {
      const id = timers.after(durationMs, resolve);
      const previous = cancelCurrent;
      cancelCurrent = (): void => {
        previous?.();
        timers.cancel(id);
        resolve();
      };
    });
  };

  /** Blocks until the mission is running again. A paused mission says nothing (R1-4). */
  const awaitUnpaused = (token: { cancelled: boolean }): Promise<void> =>
    paused && !token.cancelled
      ? new Promise<void>((resolve) => {
          resumeWaiters.push(resolve);
        })
      : Promise.resolve();

  const releaseWaiters = (): void => {
    const waiting = resumeWaiters;
    resumeWaiters = [];
    for (const resolve of waiting) resolve();
  };

  const runDelivery = async (
    token: { cancelled: boolean },
    teachingContentId: TeachingContentId,
    context: TeachingContext,
    runOptions: TeachingRunOptions,
  ): Promise<TeachingRunResult> => {
    const trigger: TeachingTrigger = runOptions.trigger ?? 'LADDER';
    let definition;
    try {
      definition = await options.provider.getTeachingMoment(teachingContentId, context);
    } catch {
      // The cap is deliberately not spent: a script this build could not load must not cost the
      // child the one full moment they are entitled to.
      return { delivery: 'NONE', outcome: 'SKIPPED' };
    }
    // Ended while the script was being fetched: nothing was spent and nothing is delivered.
    if (token.cancelled) return { delivery: 'NONE', outcome: 'SKIPPED' };

    const delivery: TeachingDelivery = consumed.has(context.playerId) ? 'HINT' : 'FULL';
    if (delivery === 'FULL') {
      consumed.add(context.playerId);
      // Spend, persist, *then* deliver (R1-3). Nothing between here and the child seeing the
      // moment may run before the spend is durable.
      await runOptions.onCapSpent?.();
      // That write is asynchronous and nothing has been said yet, so this is a window a grown-up
      // can press End inside (R3-1). The moment does not start at all; the spent cap stands, which
      // is the conservative direction — it can only cost a moment that was never delivered.
      if (token.cancelled) return { delivery, outcome: 'SKIPPED', definition };
    }

    // A hint is the opening line, spoken once. The full moment is the whole script.
    const lines = delivery === 'FULL' ? definition.narration : definition.narration.slice(0, 1);

    options.onEvent?.({
      type: 'TEACHING_STARTED',
      teachingContentId,
      playerId: context.playerId,
      delivery,
      trigger,
      // What a scene draws. Without it the visual steps the game authored had no route out of
      // the controller, so the moment was a silent wait with a voice over it (R1-2).
      definition: {
        narration: lines,
        visualSteps: definition.visualSteps,
        confirmationContentId: definition.confirmationContentId,
      },
    });

    /**
     * ONE budget for the whole moment (R1-4, TEA-01).
     *
     * Narration used to run before the visual wait even started, so a moment that declared a
     * twelve-second maximum could speak for twenty and then wait seven more. Speech is part of
     * the interruption a child experiences, so it is part of the interruption's budget — and
     * time behind a pause overlay is not, which is why the elapsed clock discounts it.
     */
    const stepDuration = definition.visualSteps.reduce((total, step) => total + step.durationMs, 0);
    const budget =
      delivery === 'FULL'
        ? Math.min(stepDuration, definition.maximumDurationMs)
        : // A hint is not an interruption; it gets the first step's worth of screen time at most.
          Math.min(definition.visualSteps[0]?.durationMs ?? 0, definition.maximumDurationMs);

    const startedAtMs = options.scheduler.now();
    const pausedAtStartMs = pausedTotalMs;
    const elapsedMs = (): number => {
      const pausedSoFar =
        pausedTotalMs + (pausedSinceMs === null ? 0 : options.scheduler.now() - pausedSinceMs);
      return Math.max(0, options.scheduler.now() - startedAtMs - (pausedSoFar - pausedAtStartMs));
    };

    /**
     * One line, raced against what is left of the budget (R1-4).
     *
     * Two things can end a line early and they mean different things. The deadline means the
     * moment has spent its whole interruption and the script stops there — a speech engine that
     * never settles its promise cannot hold a five-year-old's mission open. A pause means the
     * child is not listening, so the sentence is stopped and handed *back*: it is said again from
     * the beginning when they return, rather than being half-heard and skipped.
     */
    const speakLine = (
      line: string,
      interrupt: boolean,
      deadlineMs: number,
    ): Promise<'SPOKEN' | 'INTERRUPTED'> =>
      new Promise((resolve) => {
        let settled = false;
        let deadline: ReturnType<typeof timers.after> | null = null;

        const done = (outcome: 'SPOKEN' | 'INTERRUPTED'): void => {
          if (settled) return;
          settled = true;
          interruptLine = null;
          if (deadline !== null) timers.cancel(deadline);
          resolve(outcome);
        };

        // On the controller's own pause-aware group, so time behind the overlay is not spent.
        deadline = timers.after(deadlineMs, () => {
          options.narration.stop();
          done('SPOKEN');
        });

        interruptLine = (): void => {
          options.narration.stop();
          done('INTERRUPTED');
        };

        void options.narration.speak(line, { interrupt }).then(
          () => done('SPOKEN'),
          // A narration failure is silent by design; the moment's visuals still carry it.
          () => done('SPOKEN'),
        );
      });

    let lineIndex = 0;
    /** True while a line is being said for the second time, so it may cut its own remnant off. */
    let repeatingLine = false;
    while (lineIndex < lines.length) {
      await awaitUnpaused(token);
      // Checked immediately after every external await, never reset: a cancel that has already
      // happened cannot be overtaken by the next thing this loop waits on (R3-1).
      if (token.cancelled) break;
      const remainingMsForLine = budget - elapsedMs();
      if (remainingMsForLine <= 0) break;
      // Interrupt only on the first line: the rest follow it rather than cutting it off.
      const outcome = await speakLine(
        lines[lineIndex] ?? '',
        lineIndex === 0 || repeatingLine,
        remainingMsForLine,
      );
      if (token.cancelled) break;
      if (outcome === 'INTERRUPTED') {
        repeatingLine = true;
        continue;
      }
      repeatingLine = false;
      lineIndex += 1;
    }

    const remainingMs = budget - elapsedMs();
    if (!token.cancelled && remainingMs > 0) await wait(token, remainingMs);
    cancelCurrent = null;

    options.onEvent?.({
      type: 'TEACHING_ENDED',
      teachingContentId,
      playerId: context.playerId,
      delivery,
      trigger,
    });

    return { delivery, outcome: 'CONFIRMED', definition };
  };

  const deliver = async (
    teachingContentId: TeachingContentId,
    context: TeachingContext,
    runOptions: TeachingRunOptions = {},
  ): Promise<TeachingRunResult> => {
    // Created before the delivery's first await, so there is no instant in which a cancel has
    // nowhere to land, and removed only once the delivery is over (R3-1).
    const token = { cancelled: false };
    liveTokens.add(token);
    try {
      return await runDelivery(token, teachingContentId, context, runOptions);
    } finally {
      liveTokens.delete(token);
    }
  };

  return {
    deliver,

    async run(
      teachingContentId: TeachingContentId,
      context: TeachingContext,
    ): Promise<TeachingMomentOutcome> {
      return (await deliver(teachingContentId, context)).outcome;
    },

    isAvailableFor(playerId: PlayerId): boolean {
      return !consumed.has(playerId);
    },

    consumedPlayerIds: () => [...consumed],

    restore(playerIds: readonly PlayerId[]): void {
      consumed.clear();
      for (const playerId of playerIds) consumed.add(playerId);
    },

    pause(): void {
      if (paused) return;
      paused = true;
      pausedSinceMs = options.scheduler.now();
      timers.pause();
      // Whatever is mid-sentence stops now, and the loop will say that line again on resume.
      interruptLine?.();
    },

    resume(): void {
      if (!paused) return;
      paused = false;
      if (pausedSinceMs !== null) {
        pausedTotalMs += options.scheduler.now() - pausedSinceMs;
        pausedSinceMs = null;
      }
      timers.resume();
      releaseWaiters();
    },

    cancel(): void {
      // Every delivery that is still running, including one that has not said a word yet because
      // its cap is still being written (R3-1).
      for (const token of liveTokens) token.cancelled = true;
      // Whatever the moment is waiting on — a suspended timer, a line in the air, or the next
      // narration line — stops waiting. A grown-up who ended the sitting is not asked to press
      // Resume first.
      interruptLine?.();
      releaseWaiters();
      const stop = cancelCurrent;
      cancelCurrent = null;
      stop?.();
    },
  };
}
